/**
 * One moment on a 7R graph, as the readout card, the pinned card and the Statistics point drawer
 * show it (OE-265). Built by the graph from the data point nearest the cursor on each visible line.
 */
export interface GraphPointLine {
    line: 'primary' | 'secondary' | 'tertiary';
    /** "Total", "Sales orders", "Invoices". */
    name: string;
    colour: string;
    /** This transaction's value. */
    value: number;
    /** The running total the line is at. */
    accumulated: number;
    /** e.g. "Invoice", "Credit Note", "Carried over". */
    type?: string | null;
    repName?: string | null;
    repKey?: number | string | null;
    /** A month point lists the reps behind the day. */
    repCount?: number;
}

export interface GraphPoint {
    date: Date;
    /** Top line first, the order they sit on screen. */
    lines: GraphPointLine[];
    /** Where the target line is at this moment (absent without a target). */
    targetAt?: number;
    /** 'daily' points name a time of day, 'monthly' points a day. */
    kind: 'daily' | 'monthly';
    /** When the period on the graph ends (the right edge of the target line). */
    periodEnd?: Date;
}

export interface PointComparison {
    /** B - A in milliseconds (never negative: the earlier point is A). */
    spanMs: number;
    from: GraphPoint;
    to: GraphPoint;
    /** What each line added between the two points. */
    added: { line: GraphPointLine['line']; name: string; colour: string; amount: number }[];
    /** Total added per hour (daily) or per day (monthly); null when the span is too short to rate. */
    rate: number | null;
    rateUnit: 'hour' | 'day';
    /** What the target line asked for over the same span (absent without a target). */
    targetNeeded?: number;
    /** Total added minus targetNeeded: positive = beat the target over the span. */
    versusTarget?: number;
}

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/** Compares two points, earlier first, whichever order they were clicked in. */
export function comparePoints(a: GraphPoint, b: GraphPoint): PointComparison {
    const [from, to] = a.date.getTime() <= b.date.getTime() ? [a, b] : [b, a];
    const spanMs = to.date.getTime() - from.date.getTime();
    const added = to.lines.map((l) => {
        const before = from.lines.find((f) => f.line === l.line);
        return { line: l.line, name: l.name, colour: l.colour, amount: l.accumulated - (before?.accumulated ?? 0) };
    });
    const rateUnit = to.kind === 'monthly' ? 'day' : 'hour';
    const unitMs = rateUnit === 'day' ? DAY : HOUR;
    const total = added.find((x) => x.line === 'primary')?.amount ?? 0;
    // Under a tenth of the unit, a rate mostly reports noise.
    const rate = spanMs >= unitMs / 10 ? total / (spanMs / unitMs) : null;
    const hasTarget = from.targetAt !== undefined && to.targetAt !== undefined;
    const targetNeeded = hasTarget ? (to.targetAt as number) - (from.targetAt as number) : undefined;
    const versusTarget = targetNeeded !== undefined ? total - targetNeeded : undefined;
    return { spanMs, from, to, added, rate, rateUnit, targetNeeded, versusTarget };
}

/** Where the period closes if the Total keeps adding at the rate between the two points. */
export function projectClose(c: PointComparison): number | null {
    const end = c.to.periodEnd?.getTime();
    if (c.rate === null || !end) return null;
    const total = c.to.lines.find((l) => l.line === 'primary')?.accumulated ?? 0;
    const left = Math.max(0, end - c.to.date.getTime());
    return total + c.rate * (left / (c.rateUnit === 'day' ? DAY : HOUR));
}

/** "1h 06m", "45m", "3 days". */
export function formatSpan(ms: number, kind: GraphPoint['kind']): string {
    if (kind === 'monthly') {
        const days = Math.round(ms / DAY);
        return `${days} day${days === 1 ? '' : 's'}`;
    }
    const minutes = Math.round(ms / 60_000);
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

/** R1,234 (whole rands, en-ZA grouping with commas). */
export function rands(n: number): string {
    const sign = n < 0 ? '-' : '';
    return sign + 'R' + String(Math.round(Math.abs(n))).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const esc = (s: string) =>
    s.replace(
        /[&<>"']/g,
        (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string
    );

/** What the point was: the transaction and who made it, from the Total line (every line shares it). */
export function pointWhat(p: GraphPoint): string {
    const l = p.lines.find((x) => x.line === 'primary') || p.lines[0];
    if (!l) return '';
    const who = l.repName
        ? `${l.repName}${l.repKey ? ` (${l.repKey})` : ''}`
        : l.repCount
          ? `${l.repCount} rep${l.repCount === 1 ? '' : 's'}`
          : '';
    return [l.type || '', who].filter(Boolean).join(' · ');
}

/** The compact hover card: the moment and what it was, then one row per line with its running total. */
export function pointCardHtml(p: GraphPoint, timeLabel: string): string {
    const what = pointWhat(p);
    const rows = p.lines
        .map((l) => {
            const detail = l.value ? `${l.value > 0 ? '+' : ''}${rands(l.value)} here` : 'no change';
            return (
                `<div class="gr-row"><span class="gr-dot" style="background:${l.colour}"></span>` +
                `<span class="gr-name">${esc(l.name)}</span><span class="gr-value">${rands(l.accumulated)}</span>` +
                `<span class="gr-detail">${detail}</span></div>`
            );
        })
        .join('');
    return `<div class="gr-time">${esc(timeLabel)}</div>${what ? `<div class="gr-what">${esc(what)}</div>` : ''}${rows}`;
}
